/*
  Infinite Options bundled-product personalization carry-over.

  Problem: when a uniform package includes a personalized add-on (e.g. the backpack), Infinite
  Options adds that add-on to the cart as its own line via its own POST to /cart/add.js. That
  line only carries IO's internal properties, so Athletech cannot associate the player's name
  and number with the add-on SKU.

  Fix: Infinite Options fires "infiniteoptions.beforeVariantPost" right before each bundled
  variant is posted, with event.detail.variant ({name, productId, handle, variantId, ...}).
  IO merges event.detail.variant.properties into the properties it sends, so we copy the
  personalization fields from the uniform's product form onto the variant before the POST.

  Gate (either passes):
    1. The uniform product itself carries the trigger tag. Rendered by Liquid into
       window.SL_IO_BUNDLE_PARENT_TAGGED at page render, so it involves no runtime lookup and
       cannot be affected by caching, extensions, or timing. This is the dependable path.
    2. The add-on product carries the trigger tag. Looked up client-side from
       /products/<handle>.js (no-store, one retry), prefetched when IO loads its fields and
       again when the shopper picks the add-on.
  If the add-on lookup is still unresolved or failed at post time and the parent is not
  tagged, behaviour follows window.SL_IO_BUNDLE_FAIL_OPEN: true copies the properties anyway
  (a plain add-on with extra properties is recoverable; a personalized one with none is not),
  false skips. Either way a console warning is logged.

  Size: when the add-on variant being posted has a Size option, its selected value is also
  written to the line as the "Size" property (window.SL_IO_BUNDLE_SIZE_PROPERTY), using the
  variant data from the same /products/<handle>.js lookup. Controlled by
  window.SL_IO_BUNDLE_SIZE_ALWAYS: true adds Size to every sized add-on IO posts, false only
  when the personalization gate passes.

  SKU: the add-on variant's SKU is written to the line as "SKU1" (window.SL_IO_BUNDLE_SKU_PROPERTY),
  from the same /products/<handle>.js lookup, under the same always/gated switch as Size
  (window.SL_IO_BUNDLE_SIZE_ALWAYS).

  Configure in snippets/io-bundle-properties.liquid. Added 2026-09. AP
*/
(function () {
  if (window.SLIoBundlePropsReady) return;
  window.SLIoBundlePropsReady = true;

  var DEFAULT_TAG = 'IO Personalized Add-On';
  var DEFAULT_FIELDS = [
    'Player First Name',
    'Player Last Name',
    'Uniform Number',
    'Assigned Name',
    'Selected Team',
    'Program',
    'Season'
  ];

  // handle -> { tags: [...] | null (pending) | undefined (failed), product: <product.js JSON> }
  var tagCache = {};

  function triggerTag() {
    return String(window.SL_IO_BUNDLE_TAG || DEFAULT_TAG).trim().toLowerCase();
  }

  function fields() {
    return Array.isArray(window.SL_IO_BUNDLE_PROPS) && window.SL_IO_BUNDLE_PROPS.length
      ? window.SL_IO_BUNDLE_PROPS
      : DEFAULT_FIELDS;
  }

  function failOpen() {
    return window.SL_IO_BUNDLE_FAIL_OPEN !== false;
  }

  function parentTagged() {
    return window.SL_IO_BUNDLE_PARENT_TAGGED === true;
  }

  function sizeProperty() {
    return String(window.SL_IO_BUNDLE_SIZE_PROPERTY || 'Size');
  }

  function sizeAlways() {
    return window.SL_IO_BUNDLE_SIZE_ALWAYS !== false;
  }

  function skuProperty() {
    return String(window.SL_IO_BUNDLE_SKU_PROPERTY || 'SKU1');
  }

  // The add-on's /products/<handle>.js data, or null when not fetched yet / failed.
  function cachedProduct(handle) {
    var entry = tagCache[handle];
    return (entry && entry.product) || null;
  }

  function findVariant(product, variantId) {
    if (!product || !Array.isArray(product.variants)) return null;
    return product.variants.filter(function (v) { return String(v.id) === String(variantId); })[0] || null;
  }

  // Returns the add-on variant's SKU, or null when it has none or its data is not available yet.
  function variantSku(handle, variantId) {
    var variant = findVariant(cachedProduct(handle), variantId);
    var sku = variant && variant.sku != null ? String(variant.sku).trim() : '';
    return sku || null;
  }

  // Returns the selected value of the add-on variant's Size option, or null when the product
  // has no size option or its data is not available yet.
  function variantSize(handle, variantId) {
    var product = cachedProduct(handle);
    if (!product || !Array.isArray(product.options) || !Array.isArray(product.variants)) return null;
    var index = -1;
    product.options.forEach(function (option, i) {
      // /products/<handle>.js returns options as objects ({name, position, values}) on current
      // stores and as plain name strings on older ones; accept both.
      var name = option && typeof option === 'object' ? option.name : option;
      if (index === -1 && String(name || '').trim().toLowerCase().indexOf('size') !== -1) index = i;
    });
    if (index === -1) return null;
    var variant = findVariant(product, variantId);
    if (!variant) return null;
    var value = Array.isArray(variant.options) ? variant.options[index] : variant['option' + (index + 1)];
    value = value == null ? '' : String(value).trim();
    return value || null;
  }

  function productUrl(handle) {
    var root = (window.routes && window.routes.root_url) || '/';
    return root.replace(/\/$/, '') + '/products/' + encodeURIComponent(handle) + '.js';
  }

  function fetchProduct(handle, attempt) {
    return fetch(productUrl(handle), { headers: { Accept: 'application/json' }, cache: 'no-store', credentials: 'same-origin' })
      .then(function (r) {
        if (!r.ok) throw new Error('HTTP ' + r.status);
        return r.json();
      })
      .catch(function (e) {
        if (attempt < 1) return fetchProduct(handle, attempt + 1);
        throw e;
      });
  }

  function prefetchTags(handle) {
    if (!handle) return;
    var entry = tagCache[handle];
    if (entry && (entry.tags === null || Array.isArray(entry.tags))) return; // pending or resolved
    tagCache[handle] = { tags: null, product: null };
    fetchProduct(handle, 0)
      .then(function (product) {
        tagCache[handle].product = product || null;
        tagCache[handle].tags = product && Array.isArray(product.tags) ? product.tags : [];
      })
      .catch(function (e) {
        console.warn('io-bundle-properties: product lookup failed for', handle, e);
        tagCache[handle].tags = undefined; // failed; a later prefetch call will retry
      });
  }

  function prefetchAllBundleProducts() {
    var $ = window.Shoppad && window.Shoppad.$;
    if (!$) return;
    $('#infiniteoptions-container option, #infiniteoptions-container input').each(function () {
      var handle = $(this).data('product-handle');
      if (handle) prefetchTags(handle);
    });
  }

  // true / false when known, null when pending or failed
  function addonTagged(handle) {
    var entry = tagCache[handle];
    if (!entry || !Array.isArray(entry.tags)) return null;
    var wanted = triggerTag();
    return entry.tags.some(function (tag) { return String(tag).trim().toLowerCase() === wanted; });
  }

  function getProductForm() {
    var container = document.getElementById('infiniteoptions-container');
    var formId = container && container.getAttribute('form');
    var form = formId && document.getElementById(formId);
    if (!form) form = document.querySelector('.product__info-wrapper form[action*="/cart/add"]');
    return form;
  }

  function readProperties(form) {
    // FormData includes fields attached to the form via the form="" attribute, which is how
    // Infinite Options' own inputs (Player First/Last Name, etc.) hang off the Dawn product form.
    var data = new FormData(form);
    var out = {};
    fields().forEach(function (name) {
      var value = data.get('properties[' + name + ']');
      if (typeof value === 'string') {
        value = value.trim();
        if (value) out[name] = value;
      }
    });
    return out;
  }

  function shouldCopy(variant) {
    if (parentTagged()) return true;
    if (!variant.handle) return false;
    var tagged = addonTagged(variant.handle);
    if (tagged === true) return true;
    if (tagged === false) return false;
    console.warn('io-bundle-properties: add-on tags unknown at post time for', variant.handle,
      failOpen() ? '- copying anyway (fail open)' : '- skipping (fail closed)');
    return failOpen();
  }

  // Warm the tag cache as early as possible
  document.addEventListener('infiniteoptions.appLoad', prefetchAllBundleProducts);
  document.addEventListener('infiniteoptions.fieldLoad', prefetchAllBundleProducts);
  document.addEventListener('infiniteoptions.productBundleAdd', function (event) {
    var bundle = event.detail && event.detail.productBundle;
    if (bundle && bundle.handle) prefetchTags(bundle.handle);
  });
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', prefetchAllBundleProducts);
  } else {
    prefetchAllBundleProducts();
  }

  document.addEventListener('infiniteoptions.beforeVariantPost', function (event) {
    try {
      var variant = event.detail && event.detail.variant;
      if (!variant) return;

      var copy = shouldCopy(variant);
      var copied = {};

      if (copy) {
        var form = getProductForm();
        if (form) copied = readProperties(form);
      }

      if (copy || sizeAlways()) {
        if (variant.handle && variant.variantId) {
          if (!cachedProduct(variant.handle)) {
            console.warn('io-bundle-properties: variant data unknown at post time for', variant.handle, '- Size / SKU not added');
          }
          var size = variantSize(variant.handle, variant.variantId);
          if (size) copied[sizeProperty()] = size;
          var sku = variantSku(variant.handle, variant.variantId);
          if (sku) copied[skuProperty()] = sku;
        }
      }

      if (!Object.keys(copied).length) return;

      // Existing variant properties win, so IO-provided values are never clobbered.
      variant.properties = Object.assign(copied, variant.properties || {});
    } catch (e) {
      console.warn('io-bundle-properties', e);
    }
  });
})();
